import { Link, useParams } from 'react-router-dom';

import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';
import {
  INVESTMENT_LABELS,
  OPEX_SUBTYPE_LABELS,
  fmtUSD,
} from './rollups';
import type {
  ClassificationDecision,
  ClassifierCandidate,
  ClassifiedCommit,
} from './types';

/**
 * Per-commit audit page. Surfaces everything needed to defend the
 * classification: rule trace, candidate confidences, the
 * authoritative decision (with source pin), and the commit's
 * underlying metadata.
 *
 * This is the spec's "click-through audit trail" requirement —
 * every cell in every report links here so the classification
 * rationale stays one click away from any number.
 */
export function CommitAuditView() {
  const { sha } = useParams<{ sha: string }>();
  const { data, isPending, error } = useAccountingView();

  if (isPending) return <Skeleton title='Commit audit' rows={6} />;
  if (error) return <div className={styles.errorBox}>Failed to load accounting view: {error.message}</div>;
  if (!data || !sha) return null;

  const commit = data.commits.find((c) => c.sha === sha) ?? null;
  if (!commit) {
    return (
      <>
        <Link to='/accounting/review' className={styles.backLink}>
          ← Back
        </Link>
        <div className={styles.errorBox}>
          No commit with SHA <code>{sha}</code> in the current period.
        </div>
      </>
    );
  }

  const author = data.engineers.find((e) => e.id === commit.author_engineer_id) ?? null;
  const project = commit.decision.accounting_project_id
    ? data.accounting_projects.find((p) => p.id === commit.decision.accounting_project_id) ?? null
    : null;
  const businessFunction =
    data.business_functions.find((bf) => bf.id === commit.business_function_id) ?? null;
  const reviewer = commit.decision.decided_by_user_id
    ? data.engineers.find((e) => e.id === commit.decision.decided_by_user_id) ?? null
    : null;

  return (
    <>
      <Link to='/accounting' className={styles.backLink}>
        ← Back
      </Link>
      <h2 className='section-h2' style={{ marginTop: 0 }}>
        {commit.subject}
      </h2>
      <p className='muted' style={{ marginTop: 0 }}>
        <a href={commit.url} target='_blank' rel='noreferrer' className={styles.linkPlain}>
          {commit.sha_short}
        </a>{' '}
        · {commit.repo} · by {author?.display_name ?? '—'}
        {author?.kind === 'agent' ? ' 🤖' : ''}
      </p>

      <div className={styles.auditWrap}>
        <div>
          <section className={styles.auditSection}>
            <h2>Investment type — candidates</h2>
            {commit.suggestion.investment_type_candidates.map((cand, idx) => (
              <Candidate
                key={cand.type}
                cand={cand}
                label={INVESTMENT_LABELS[cand.type]}
                isWinner={idx === 0}
              />
            ))}
          </section>

          <section className={styles.auditSection}>
            <h2>Accounting treatment — candidates</h2>
            {commit.suggestion.treatment_candidates.map((cand, idx) => (
              <Candidate
                key={cand.type}
                cand={cand}
                label={cand.type === 'capex' ? 'CapEx' : 'OpEx'}
                isWinner={idx === 0}
              />
            ))}
          </section>

          <section className={styles.auditSection}>
            <h2>Rule trace</h2>
            {commit.suggestion.rule_trace.length === 0 ? (
              <p className='muted'>No rules fired — classifier returned a defensive fallback.</p>
            ) : (
              <ul className={styles.ruleList}>
                {commit.suggestion.rule_trace.map((r) => (
                  <li key={r.rule_id} className={styles.ruleItem}>
                    <div className={styles.ruleId}>{r.rule_id}</div>
                    <div className={styles.ruleDesc}>{r.rule_description}</div>
                    <div className={styles.ruleInput}>{r.matched_input}</div>
                  </li>
                ))}
              </ul>
            )}
            <p className='muted' style={{ fontSize: 12, marginTop: 'var(--3u)' }}>
              Classifier version: <code>{commit.suggestion.classifier_version}</code>
            </p>
          </section>
        </div>

        <aside>
          <DecisionCard decision={commit.decision} commit={commit} reviewerName={reviewer?.display_name ?? null} projectDisplay={project?.display ?? null} />

          <section className={styles.auditSection}>
            <h2>Commit details</h2>
            <dl className={styles.auditMetaGrid}>
              <dt>SHA</dt>
              <dd>{commit.sha_short}</dd>
              <dt>Repo</dt>
              <dd>{commit.repo}</dd>
              <dt>Files</dt>
              <dd>{commit.files_changed}</dd>
              <dt>Additions</dt>
              <dd>+{commit.additions}</dd>
              <dt>Deletions</dt>
              <dd>−{commit.deletions}</dd>
              <dt>Cost share</dt>
              <dd>{fmtUSD(commit.cost_usd)}</dd>
              <dt>Business fn</dt>
              <dd>{businessFunction?.display ?? '—'}</dd>
              <dt>Project</dt>
              <dd>{project?.display ?? <span className='muted'>—</span>}</dd>
            </dl>
          </section>
        </aside>
      </div>
    </>
  );
}

function Candidate({
  cand,
  label,
  isWinner,
}: {
  cand: ClassifierCandidate<string>;
  label: string;
  isWinner: boolean;
}) {
  const pct = Math.round(cand.confidence * 100);
  return (
    <div className={`${styles.candidateRow} ${isWinner ? styles.candidateWinner : ''}`}>
      <span className={styles.candidateLabel}>{label}</span>
      <span className={styles.candidateBar}>
        <span className={styles.candidateBarFill} style={{ right: `${100 - pct}%` }} />
      </span>
      <span className={styles.candidatePct}>{pct}%</span>
    </div>
  );
}

function DecisionCard({
  decision,
  commit,
  reviewerName,
  projectDisplay,
}: {
  decision: ClassificationDecision;
  commit: ClassifiedCommit;
  reviewerName: string | null;
  projectDisplay: string | null;
}) {
  const treatmentText =
    decision.accounting_treatment === 'capex'
      ? 'CapEx'
      : `OpEx · ${decision.opex_subtype ? OPEX_SUBTYPE_LABELS[decision.opex_subtype] : '—'}`;
  return (
    <section className={styles.auditSection} style={{ marginTop: 0 }}>
      <h2>Authoritative decision</h2>
      <div className={styles.decisionCard}>
        <span className={`${styles.decisionPin} ${styles[decision.source]}`}>
          {decision.source === 'user_pin'
            ? `Pinned by ${reviewerName ?? 'user'}`
            : decision.source === 'system_auto'
              ? 'System auto-classified'
              : 'System fallback'}
        </span>
        <dl>
          <div className={styles.decisionRow}>
            <dt>Investment type</dt>
            <dd>{INVESTMENT_LABELS[decision.investment_type]}</dd>
          </div>
          <div className={styles.decisionRow}>
            <dt>Treatment</dt>
            <dd>{treatmentText}</dd>
          </div>
          {decision.accounting_treatment === 'capex' ? (
            <div className={styles.decisionRow}>
              <dt>Cap project</dt>
              <dd>{projectDisplay ?? '—'}</dd>
            </div>
          ) : null}
          {commit.flagged_for_review ? (
            <div className={styles.decisionRow}>
              <dt>Status</dt>
              <dd>Flagged for review</dd>
            </div>
          ) : null}
        </dl>
        {decision.rationale ? (
          <div className={styles.decisionRationale}>“{decision.rationale}”</div>
        ) : null}
      </div>
    </section>
  );
}
